import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	EMPTY_SEARCH_ITEMS,
	type ResultSuppliers,
} from '../../product-router/product/result-provider/result-provider-types';
import { type SearchItems } from '../../product-router/product/result-types';
import { useProductContext } from '../../product-router/product-router';
import { APIStates } from '../product-state-machine-types';
import { type AllFilters } from '../../filters/filter-types';
import { type Workspace } from '../../../common/search-config-provider/search-config-types';

interface UseInvocationsProps {
	id: string;
	onRetry?: () => void;
}

interface DebounceHookProps {
	debounceTime: number;
}

interface PostQueryInvocationProps {
	isPreQueryFiltersEnabled: boolean;
	setPostQueryItems: (items: SearchItems) => void;
	setPostQueryAPIState: (state: APIStates) => void;
}

interface PreQueryInvocationProps {
	isPrefetchingEnabled: boolean;
	isPreQueryFiltersEnabled: boolean;
}
interface PostQueryInvocationContextProps {}

interface ProductContextProps {
	sectionIds: string[];
	isDisplayed: boolean;
	preventDebouncedSearch?: boolean;
}

interface ContextProps {
	query: string;
	analyticsInfo: {
		queryVersion: number;
		searchSessionId: string;
	};
}

interface FilterProps {
	filters: AllFilters[];
}

export type PostQueryProps = PostQueryInvocationProps &
	ContextProps &
	FilterProps &
	ProductContextProps &
	DebounceHookProps &
	PostQueryInvocationContextProps &
	Pick<ResultSuppliers, 'postQueryItemSupplier'> & { workspaces?: Workspace[] };

const useDebouncedState = ({
	debounceTime,
}: DebounceHookProps): [boolean, (value: boolean) => void, () => void] => {
	const [triggerDebounce, setTriggerDebounce] = useState<boolean>(false);

	const debouncedSetter = useMemo(
		() =>
			debounce(() => {
				setTriggerDebounce(true);
			}, debounceTime),
		[debounceTime, setTriggerDebounce],
	);

	return [triggerDebounce, setTriggerDebounce, debouncedSetter];
};

export const usePostQueryInvocation: ({
	debounceTime,
	query,
	analyticsInfo,
	filters,
	workspaces,
	sectionIds,
	isDisplayed,
	postQueryItemSupplier,
	setPostQueryItems,
	setPostQueryAPIState,
	preventDebouncedSearch,
	isPreQueryFiltersEnabled,
}: PostQueryProps) => void = ({
	debounceTime,
	query,
	analyticsInfo,
	filters,
	workspaces,
	sectionIds,
	isDisplayed,
	postQueryItemSupplier,
	setPostQueryItems,
	setPostQueryAPIState,
	preventDebouncedSearch,
	isPreQueryFiltersEnabled,
}: PostQueryProps) => {
	// Store the previous isDisplayed for this product. It is initialised to
	// account for the case where the product is initially displayed. In this
	// case, we want to fire the postQueryItemSupplier immediately.
	const previousIsDisplayed = useRef<boolean>(false);

	// Store the previous query. The default here is an undefined string here to
	// support the case where the initial state of the component is a non-empty
	// string. In this case, we want to fire the postQueryItemSupplier
	// immediately.
	const previousQuery = useRef<string | undefined>(undefined);

	const previousFilters = useRef(filters);

	const [triggerDebounce, setTriggerDebounce, debouncedSetter] = useDebouncedState({
		debounceTime,
	});

	useResetPostQueryAPIStateOnEmptySearch({
		query,
		filters,
		setPostQueryAPIState,
		isDisplayed,
		isPreQueryFiltersEnabled,
	});

	useResetPostQueryItemOnNewSearch({
		query,
		filters,
		setPostQueryItems,
		setPostQueryAPIState,
		isDisplayed,
		debouncedSetter,
		sectionIds,
		isPreQueryFiltersEnabled,
	});

	useEffect(() => {
		const queryChanged = query !== previousQuery.current;
		const filtersChanged = !isEqual(previousFilters.current, filters);
		const isDisplayedChanged = previousIsDisplayed.current === false && isDisplayed;
		const search = query || (isPreQueryFiltersEnabled && filters.length);

		if (preventDebouncedSearch && search && isDisplayed) {
			if (queryChanged) {
				previousQuery.current = query;
				setPostQueryAPIState(APIStates.Typing);
			}
			if (isDisplayedChanged || filtersChanged) {
				previousFilters.current = filters;
				postQueryItemSupplier({
					query: query,
					filters,
					analyticsInfo,
					sectionIds,
					workspaces,
				}).catch((ignore) => {});
			}
		} else if (
			// Fire when debounce is finished or when product becomes active
			(triggerDebounce || isDisplayedChanged) &&
			// Fire when search parameters change or when product becomes active
			(queryChanged || filtersChanged) &&
			search &&
			isDisplayed &&
			sectionIds
		) {
			previousQuery.current = query;
			previousFilters.current = filters;
			postQueryItemSupplier({
				query: query,
				filters,
				analyticsInfo,
				sectionIds,
				workspaces,
			}).catch((ignore) => {});
			setTriggerDebounce(false);
		}

		previousIsDisplayed.current = isDisplayed;

		// In the case of an empty query /  no filters-only search, we want to reset any previous query caches
		// Still potentially not optimal as you would not get the cache behaviour if you
		// clear the query when you're on a different tab. It shouldn't feel jank though.
		if (!search) {
			previousQuery.current = '';
			previousFilters.current = [];
		}
	}, [
		triggerDebounce,
		setTriggerDebounce,
		postQueryItemSupplier,
		sectionIds,
		query,
		analyticsInfo,
		filters,
		isDisplayed,
		workspaces,
		preventDebouncedSearch,
		isPreQueryFiltersEnabled,
		setPostQueryAPIState,
	]);
};

interface ResetPostQueryProps {
	query: string;
	filters: AllFilters[];
	isDisplayed: boolean;
	isPreQueryFiltersEnabled: boolean;
}

interface ResetPostQueryOnNewSearch extends ResetPostQueryProps {
	debouncedSetter: () => void;
}

const useResetPostQueryItemOnNewSearch = ({
	query,
	filters,
	isDisplayed,
	setPostQueryItems,
	setPostQueryAPIState,
	debouncedSetter,
	sectionIds,
	preventDebouncedSearch,
	isPreQueryFiltersEnabled,
}: ResetPostQueryOnNewSearch &
	PostQueryInvocationProps &
	Pick<ProductContextProps, 'sectionIds' | 'preventDebouncedSearch'>) => {
	// We want to retain the last search query value to avoid an infinite loop of postQueryItemSupplier
	const queryRef = useRef<string>(query);
	const filtersRef = useRef<AllFilters[]>(filters);

	useEffect(() => {
		if (
			(queryRef.current !== query || !isEqual(filtersRef.current, filters)) &&
			(query || (isPreQueryFiltersEnabled && filters.length)) &&
			isDisplayed &&
			sectionIds &&
			sectionIds.length > 0
		) {
			// reset post query items when we start a new search
			setPostQueryItems(EMPTY_SEARCH_ITEMS);
			// If debounced search is prevented we set the state to typing instead of loading
			if (preventDebouncedSearch) {
				// Typing state means that we don't fire the supplier on debounce and the user types their query
				setPostQueryAPIState(APIStates.Typing);
			} else {
				setPostQueryAPIState(APIStates.Loading);
			}
			debouncedSetter();
		}

		queryRef.current = query;
		filtersRef.current = filters;
	}, [
		query,
		isDisplayed,
		sectionIds,
		setPostQueryItems,
		setPostQueryAPIState,
		debouncedSetter,
		queryRef,
		filters,
		filtersRef,
		preventDebouncedSearch,
		isPreQueryFiltersEnabled,
	]);
};

const useResetPostQueryAPIStateOnEmptySearch = ({
	query,
	filters,
	isDisplayed,
	setPostQueryAPIState,
	preventDebouncedSearch,
	isPreQueryFiltersEnabled,
}: Pick<PostQueryInvocationProps, 'setPostQueryAPIState'> &
	ResetPostQueryProps &
	Pick<ProductContextProps, 'preventDebouncedSearch'>) => {
	// We want to retain the last search query value to avoid an infinite loop of postQueryItemSupplier
	const queryRef = useRef<string>(query);
	const filtersRef = useRef<AllFilters[]>(filters);

	useEffect(() => {
		// These two checks both assume query === '' so it's checked independanctly in the if condition below
		const queryRemoved =
			queryRef.current !== '' && (!isPreQueryFiltersEnabled || filters.length === 0);
		const filtersRemoved =
			isPreQueryFiltersEnabled && filters.length === 0 && !isEqual(filtersRef.current, []);

		// If the user returns to the pre-query state by removing the query or by clearing the filters when there isn't a query
		if (isDisplayed && query === '' && (queryRemoved || filtersRemoved)) {
			// Reset the post query api state to typing if debounced search is off, and loading is debounced search is on
			if (preventDebouncedSearch) {
				setPostQueryAPIState(APIStates.Typing);
			} else {
				setPostQueryAPIState(APIStates.Loading);
			}
		}
		queryRef.current = query;
		filtersRef.current = filters;
	}, [
		query,
		isDisplayed,
		setPostQueryAPIState,
		queryRef,
		filtersRef,
		preventDebouncedSearch,
		filters,
		isPreQueryFiltersEnabled,
	]);
};

export type PreQueryProps = PreQueryInvocationProps &
	FilterProps &
	ProductContextProps & { workspaces?: Workspace[] } & Pick<
		ResultSuppliers,
		'preQueryItemSupplier'
	>;

export const usePreQueryInvocation: ({
	filters,
	isPrefetchingEnabled,
	isPreQueryFiltersEnabled,
	sectionIds,
	preQueryItemSupplier,
	isDisplayed,
	workspaces,
}: PreQueryProps) => void = ({
	filters,
	isPrefetchingEnabled,
	isPreQueryFiltersEnabled,
	sectionIds,
	preQueryItemSupplier,
	isDisplayed,
	workspaces,
}: PreQueryProps) => {
	/**
	 * Even in case of cache warming we want to track the product state hence we should always pass the decorated supplier. Note: Both decorated and non decorated suppliers have the same signature.
	 */

	useEffect(() => {
		if (
			(!isPreQueryFiltersEnabled || filters.length === 0) &&
			(isPrefetchingEnabled === true || isDisplayed === true) &&
			sectionIds &&
			sectionIds.length > 0
		) {
			preQueryItemSupplier({ sectionIds, workspaces }).catch((ignore) => {});
		}
	}, [
		isPrefetchingEnabled,
		isPreQueryFiltersEnabled,
		isDisplayed,
		sectionIds,
		preQueryItemSupplier,
		workspaces,
		filters.length,
	]);
};

export type PreQueryRetryArgs = {
	onRetry?: () => void;
} & ProductContextProps & { workspaces?: Workspace[] } & Pick<
		ResultSuppliers,
		'preQueryItemSupplier'
	>;

export const useOnRetryPreQueryInvocation: ({
	preQueryItemSupplier,
	onRetry,
	sectionIds,
	workspaces,
}: PreQueryRetryArgs) => () => void = ({
	preQueryItemSupplier,
	onRetry,
	sectionIds,
	workspaces,
}: PreQueryRetryArgs) => {
	return useCallback(() => {
		preQueryItemSupplier({ sectionIds, workspaces });
		onRetry?.();
	}, [onRetry, preQueryItemSupplier, sectionIds, workspaces]);
};

export type PostQueryRetryArgs = {
	onRetry?: () => void;
	filters: AllFilters[];
	workspaces?: Workspace[];
} & ContextProps &
	ProductContextProps &
	Pick<ResultSuppliers, 'postQueryItemSupplier'>;

export const useOnRetryPostQueryInvocation: ({
	postQueryItemSupplier,
	onRetry,
	query,
	analyticsInfo,
	filters,
	sectionIds,
	workspaces,
}: PostQueryRetryArgs) => () => void = ({
	postQueryItemSupplier,
	onRetry,
	query,
	analyticsInfo,
	filters,
	sectionIds,
	workspaces,
}: PostQueryRetryArgs) => {
	return useCallback(() => {
		postQueryItemSupplier({
			query,
			analyticsInfo,
			sectionIds,
			filters,
			workspaces,
		});
		onRetry?.();
	}, [postQueryItemSupplier, query, analyticsInfo, sectionIds, filters, workspaces, onRetry]);
};

export const useInvocations: ({
	id,
	debounceTime,
	query,
	analyticsInfo,
	filters,
	isPrefetchingEnabled,
	isPreQueryFiltersEnabled,
	workspaces,
	onRetry,
	preQueryItemSupplier,
	postQueryItemSupplier,
	setPostQueryItems,
	setPostQueryAPIState,
}: UseInvocationsProps &
	ResultSuppliers &
	PostQueryInvocationProps &
	PreQueryInvocationProps &
	DebounceHookProps &
	FilterProps &
	ContextProps & {
		workspaces?: Workspace[];
	}) => {
	onRetryPreQuery: () => void;
	onRetryPostQuery: () => void;
} = ({
	id,
	debounceTime,
	query,
	analyticsInfo,
	filters,
	isPrefetchingEnabled,
	isPreQueryFiltersEnabled,
	workspaces,
	onRetry,
	preQueryItemSupplier,
	postQueryItemSupplier,
	setPostQueryItems,
	setPostQueryAPIState,
}: UseInvocationsProps &
	ResultSuppliers &
	PostQueryInvocationProps &
	PreQueryInvocationProps &
	DebounceHookProps &
	FilterProps &
	ContextProps & {
		workspaces?: Workspace[];
	}) => {
	const { getProduct } = useProductContext();
	const product = getProduct(id);
	const isDisplayed = product?.isDisplayed || false;
	const sectionIds = useMemo(() => product?.sectionIds || [], [product]);
	const preventDebouncedSearch = !!product?.showSearchOnEnter;
	const invokePostQuerySearchRef = product?.invokePostQuerySearchRef;

	const invokePostQueryItemSupplier = useCallback(() => {
		postQueryItemSupplier({
			query: query,
			filters,
			analyticsInfo,
			sectionIds,
			workspaces,
		}).catch((ignore) => {});
	}, [analyticsInfo, filters, postQueryItemSupplier, query, sectionIds, workspaces]);

	if (invokePostQuerySearchRef) {
		invokePostQuerySearchRef.current = invokePostQueryItemSupplier;
	}

	usePostQueryInvocation({
		isDisplayed,
		debounceTime,
		postQueryItemSupplier,
		query,
		analyticsInfo,
		filters,
		sectionIds,
		setPostQueryItems,
		setPostQueryAPIState,
		workspaces,
		preventDebouncedSearch: !!preventDebouncedSearch,
		isPreQueryFiltersEnabled,
	});

	usePreQueryInvocation({
		isDisplayed,
		preQueryItemSupplier,
		filters,
		sectionIds,
		isPrefetchingEnabled,
		isPreQueryFiltersEnabled,
		workspaces,
	});

	const updateStateOnRetryPreQuery = useOnRetryPreQueryInvocation({
		preQueryItemSupplier,
		onRetry,
		sectionIds,
		isDisplayed,
		workspaces,
	});

	const updateStateOnRetryPostQuery = useOnRetryPostQueryInvocation({
		postQueryItemSupplier,
		onRetry,
		query,
		analyticsInfo,
		filters,
		sectionIds,
		isDisplayed,
		workspaces,
	});

	return {
		onRetryPreQuery: updateStateOnRetryPreQuery,
		onRetryPostQuery: updateStateOnRetryPostQuery,
	};
};
